import { UNIT_CODES } from '../contract/types';
import {
  FAHRENHEIT_TO_CELSIUS_FACTOR,
  FAHRENHEIT_TO_CELSIUS_OFFSET,
  GR_PER_MI_TO_GR_PER_KM,
  LB_PER_MI_TO_GR_PER_KM,
  MI_PER_KWH_TO_KWH_PER_100KM,
  MILE_KM,
  MPG_UK_TO_L_PER_100KM,
  MPG_US_TO_L_PER_100KM,
  MPS_TO_KMH,
  POUND_KG,
  PSI_KPA,
  UK_GALLON_L,
  US_GALLON_L,
} from './constants';

/**
 * What every quantity is, and how each of its units relates to one canonical unit.
 *
 * Conversions are not stored per pair. Each unit declares a single transform *to* its kind's
 * canonical unit, and `convert` composes two of them: source to canonical, canonical to target.
 * With n units that is n declarations rather than n squared, and — more usefully — there is no way
 * to declare a pair inconsistently with the rest, because no pair is declared at all.
 *
 * The three transform families are not decoration. Treating every conversion as "multiply by a
 * factor" is correct for seven kinds and silently wrong for three: temperature carries an offset,
 * and the two consumption kinds *invert*. A reciprocal unit converted as though it were linear
 * produces a plausible number that is wrong in the wrong direction, which is the hardest kind of
 * defect to notice on a screen.
 */

const QUANTITY_CODES = UNIT_CODES;

/**
 * A quantity this layer can convert.
 *
 * Coincides with {@link MeasurementKind} today, and is kept as its own name because the two answer
 * different questions: a kind is something a preference can name, a quantity is something the
 * registry knows how to convert. Nothing requires the registry to convert every unit a preference
 * can express, and a kind added for its label alone would need no entry below.
 */
export type QuantityKind = keyof typeof QUANTITY_CODES;

/** The unit codes belonging to one kind. Narrowed per kind, so units cannot cross quantities. */
export type UnitFor<K extends QuantityKind> =
  (typeof QUANTITY_CODES)[K][number];

/**
 * How one unit relates to its canonical unit.
 *
 * Each describes the *forward* direction, unit to canonical. `convert` derives the inverse, so a
 * declaration cannot disagree with itself.
 */
export type UnitTransform =
  | { readonly family: 'linear'; readonly factor: number }
  | {
      readonly family: 'affine';
      readonly factor: number;
      readonly offset: number;
    }
  | { readonly family: 'reciprocal'; readonly numerator: number };

export type UnitTransformFamily = UnitTransform['family'];

/** The canonical unit's own transform, and the no-op every kind needs exactly once. */
const IDENTITY: UnitTransform = Object.freeze({ family: 'linear', factor: 1 });

const linear = (factor: number): UnitTransform =>
  Object.freeze({ family: 'linear', factor });

const reciprocal = (numerator: number): UnitTransform =>
  Object.freeze({ family: 'reciprocal', numerator });

type KindSpec<K extends QuantityKind> = {
  /** The unit every other unit of this kind is defined against. */
  readonly canonical: UnitFor<K>;
  /**
   * Every unit of the kind. Typed as a total `Record`, so a code added to the preference contract
   * fails to compile here until it is given a transform — the same exhaustiveness the signature
   * uses to stop a preference escaping the memo key.
   */
  readonly units: Readonly<Record<UnitFor<K>, UnitTransform>>;
};

/**
 * Canonical units are the metric member of each kind.
 *
 * The choice is arithmetically arbitrary — composition works from any anchor — so it is made on
 * legibility. Metric is what most endpoints already return, and for the two consumption kinds it
 * fixes the canonical orientation as *consumption per distance* rather than distance per unit
 * consumed, which keeps `L_PER_100KM` and `KWH_PER_100KM` reading the same way round.
 */
export const REGISTRY: { readonly [K in QuantityKind]: KindSpec<K> } = {
  distance: {
    canonical: 'KM',
    units: Object.freeze({
      KM: IDENTITY,
      MILES: linear(MILE_KM),
    }),
  },

  speed: {
    canonical: 'KMH',
    units: Object.freeze({
      KMH: IDENTITY,
      MPH: linear(MILE_KM),
      MPS: linear(MPS_TO_KMH),
    }),
  },

  capacity: {
    canonical: 'LITER',
    units: Object.freeze({
      LITER: IDENTITY,
      GAL_US: linear(US_GALLON_L),
      GAL_UK: linear(UK_GALLON_L),
    }),
  },

  /**
   * Reciprocal. `MPG_UK` to `MPG_US` therefore inverts twice and is linear overall, which falls out
   * of composing the two declarations rather than needing a case of its own.
   */
  fuelConsumption: {
    canonical: 'L_PER_100KM',
    units: Object.freeze({
      L_PER_100KM: IDENTITY,
      MPG_US: reciprocal(MPG_US_TO_L_PER_100KM),
      MPG_UK: reciprocal(MPG_UK_TO_L_PER_100KM),
    }),
  },

  /** Reciprocal for the same reason: miles per kWh is an efficiency, kWh per 100 km a consumption. */
  electricConsumption: {
    canonical: 'KWH_PER_100KM',
    units: Object.freeze({
      KWH_PER_100KM: IDENTITY,
      MI_PER_KWH: reciprocal(MI_PER_KWH_TO_KWH_PER_100KM),
    }),
  },

  /**
   * One unit, so every conversion is the identity. Registered anyway: the wire can carry an energy
   * code, callers should not have to special-case the kind, and the day a second code appears the
   * only change needed here is one line.
   */
  energy: {
    canonical: 'KWH',
    units: Object.freeze({
      KWH: IDENTITY,
    }),
  },

  pressure: {
    canonical: 'KPA',
    units: Object.freeze({
      KPA: IDENTITY,
      PSI: linear(PSI_KPA),
    }),
  },

  /** The only affine kind. The offset is why doubling a temperature does not survive conversion. */
  temperature: {
    canonical: 'C',
    units: Object.freeze({
      C: IDENTITY,
      F: Object.freeze({
        family: 'affine',
        factor: FAHRENHEIT_TO_CELSIUS_FACTOR,
        offset: FAHRENHEIT_TO_CELSIUS_OFFSET,
      }),
    }),
  },

  /** Linear: a mass spread over a distance scales like any other ratio of two linear quantities. */
  emission: {
    canonical: 'GR_PER_KM',
    units: Object.freeze({
      GR_PER_KM: IDENTITY,
      GR_PER_MI: linear(GR_PER_MI_TO_GR_PER_KM),
      LB_PER_MI: linear(LB_PER_MI_TO_GR_PER_KM),
    }),
  },

  mass: {
    canonical: 'KG',
    units: Object.freeze({
      KG: IDENTITY,
      LB: linear(POUND_KG),
    }),
  },
};

Object.freeze(REGISTRY);
for (const spec of Object.values(REGISTRY)) {
  Object.freeze(spec);
}

export const QUANTITY_KINDS: readonly QuantityKind[] = Object.freeze(
  Object.keys(REGISTRY) as QuantityKind[]
);

/** The unit every other unit of the kind is defined against. */
export const canonicalUnitFor = <K extends QuantityKind>(kind: K): UnitFor<K> =>
  REGISTRY[kind].canonical;

/** Every unit code the kind recognises. */
export const unitsOf = <K extends QuantityKind>(kind: K): UnitFor<K>[] =>
  Object.keys(REGISTRY[kind].units) as UnitFor<K>[];

/** How the unit relates to its canonical unit. */
export const transformFor = <K extends QuantityKind>(
  kind: K,
  unit: UnitFor<K>
): UnitTransform => REGISTRY[kind].units[unit];

/**
 * Whether the kind has any unit that is not a plain scale.
 *
 * Exposed because "is this safe to multiply?" is a question callers legitimately need to ask
 * before doing arithmetic on a converted value, and because the answer is a property of the
 * registry rather than something a feature should be keeping its own list of.
 */
export const isLinearKind = (kind: QuantityKind): boolean =>
  Object.values(REGISTRY[kind].units).every(
    ({ family }) => family === 'linear'
  );
